import { Customer } from "@lib/types/Customer";
import { CustomerLead } from "@lib/types/CustomerLead";
import { AllCurrencies } from ".";

// ------------------------------------------------------------
// Common
// ------------------------------------------------------------
export enum WalletAddressCreationType {
  SINGLE = "single",
  MULTIPLE = "multiple",
}

export enum WalletAddressType {
  BTC = "BTC",
  ETH = "ETH",
  USDT = "USDT",
}

export enum WalletAddressProtocol {
  Native = "Native",
  ERC20 = "ERC20",
  TRC20 = "TRC20",
  BEP20 = "BEP20",
}

export enum WalletType {
  HOT = "hot",
  COLD = "cold",
  // HARDWARE = "hardware",
  // PAPER = "paper",
  // MULTISIG = "multisig",
  // CUSTODIAL = "custodial",
}

export const WalletAddressProtocolsWithNetwork = {
  [WalletAddressType.BTC]: [WalletAddressProtocol.Native],
  [WalletAddressType.ETH]: [
    WalletAddressProtocol.Native,
    WalletAddressProtocol.ERC20,
  ],
  [WalletAddressType.USDT]: [
    WalletAddressProtocol.ERC20,
    WalletAddressProtocol.TRC20,
    WalletAddressProtocol.BEP20,
  ],
} as const;

export type CoinProtocolMap = {
  [WalletAddressType.BTC]: WalletAddressProtocol.Native;
  [WalletAddressType.ETH]:
    | WalletAddressProtocol.Native
    | WalletAddressProtocol.ERC20;
  [WalletAddressType.USDT]:
    | WalletAddressProtocol.ERC20
    | WalletAddressProtocol.TRC20
    | WalletAddressProtocol.BEP20;
};

type WalletAddressRule =
  | {
      network: WalletAddressType.BTC;
      protocol: WalletAddressProtocol.Native;
    }
  | {
      network: WalletAddressType.ETH;
      protocol: WalletAddressProtocol.Native | WalletAddressProtocol.ERC20;
    }
  | {
      network: WalletAddressType.USDT;
      protocol:
        | WalletAddressProtocol.Native
        | WalletAddressProtocol.ERC20
        | WalletAddressProtocol.TRC20
        | WalletAddressProtocol.BEP20;
    };

type BaseWallet = {
  id: string;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;

  name: string;
  type: WalletType;
  lastCustomId: number | null;
};

type BaseWalletAddress = {
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;

  address: string;
  balance: string;
  customId: number;
  disabled: boolean;
  lastScanDate: string | null;
  network: WalletAddressType;
  protocol: WalletAddressProtocol;

  extraData: null | {
    viewUrl?: string;
  };
};
// ------------------------------------------------------------
//
//
// ------------------------------------------------------------
// Billing Wallet
// ------------------------------------------------------------
export type BillingWallet = BaseWallet;

export enum BillingWalletAddressStatus {
  Available = "Available",
  InUse = "InUse",
  Reserved = "Reserved",
  Disabled = "Disabled",
}

export type BillingWalletAddress = BaseWalletAddress &
  WalletAddressRule & {
    status: BillingWalletAddressStatus;
    walletId: string;
    wallet?: BillingWallet;
    customerId: string | null;
    customer?: Customer;
    customerUserId: string | null;
    customerUser?: CustomerLead;
  };
// ------------------------------------------------------------
//
//
// ------------------------------------------------------------
// SendByte Wallet
// ------------------------------------------------------------
export enum SendByteWalletUsage {
  WhiteLabel = "WhiteLabel",
  Lead = "Lead",
}

export enum SendByteWalletAddressStatus {
  Available = "Available",
  InUse = "InUse",
  Reserved = "Reserved",
  Disabled = "Disabled",
}

export type SendByteWallet = BaseWallet & {
  usage: SendByteWalletUsage;
  customerId: string | null;
  customer?: Customer;
};

export type SendByteWalletAddress = BaseWalletAddress &
  WalletAddressRule & {
    status: SendByteWalletAddressStatus;
    walletId: string;
    wallet?: SendByteWallet;
    customerId: string | null;
    customer?: Customer;
    remainingBalance: string;
    customerUserId: string | null;
    customerUser?: CustomerLead;
  };

// ------------------------------------------------------------
//
//
// ------------------------------------------------------------
// AI Wallet
// ------------------------------------------------------------
export type AIWallet = {
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;

  name: string;
  type: WalletType;
  customerId: string | null;
  customer?: Customer;
  lastCustomId: number | null;
};

export enum AIWalletAddressStatus {
  Available = "Available",
  InUse = "InUse",
  Reserved = "Reserved",
  Disabled = "Disabled",
}

export type AIWalletAddress = BaseWalletAddress &
  WalletAddressRule & {
    status: AIWalletAddressStatus;
    walletId: string;
    wallet?: AIWallet;
    customerId: string | null;
    customer?: Customer;
    remainingBalance: string;
    freeCredit: number;
  };

export enum AIBlockchainPaymentHistoryStatus {
  UnmatchedWallet = "UnmatchedWallet",
  InvoiceNotFound = "InvoiceNotFound",
  Success = "Success",
  PendingForScan = "PendingForScan",
  ScanError = "ScanError",
  FundsNotFound = "FundsNotFound",
}

export type AIBlockchainPaymentHistory = {
  id: string;

  walletAddressId: string | null;
  walletAddress?: AIWalletAddress;

  customerId: string | null;
  customer?: Customer;

  status: AIBlockchainPaymentHistoryStatus;
  amount: number;
  network: WalletAddressType;
  error: string | null;

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;

  extraData: null | {
    address?: string;
    addressLastScan?: string;
    scanned?: {
      lastTxAmount?: number;
      total?: number;
    };
  };
};

export type AIBlockchainPayment = {
  id: string;

  customerId: string | null;
  customer?: Customer;

  isFreeCredit: boolean;

  walletAddressId: string;
  walletAddress?: AIWalletAddress;

  txId: string;
  amount: string;
  confirmed: string;
  confirmedDate: string;
  fromWalletAddress: string;
  tokenType: string;

  contractType: string;
  convertedAmount: string | null;
  convertedRate: string | null;
  convertedTo: AllCurrencies | null;

  extraData?: {
    transactionDetailUrl?: string;
    transaction_id?: string;
  };

  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
};
